import { MAX_UPSTREAM_SKU_COST_MULTIPLIER } from '@nadoutong/contracts';
import type { UpstreamModelCostWindow, UpstreamModelCostWindowInput } from '@nadoutong/contracts';

export const WEEKDAY_BITS = [0, 1, 2, 3, 4, 5, 6] as const;
export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export interface WindowDraft {
  readonly key: string;
  readonly windowId: string | undefined;
  readonly label: string;
  readonly timezone: string;
  readonly daysOfWeek: number;
  readonly startClock: string;
  readonly endClock: string;
  readonly costMultiplier: string;
  readonly inputCostMultiplier: string;
  readonly outputCostMultiplier: string;
  readonly cacheReadCostMultiplier: string;
  readonly cacheWriteCostMultiplier: string;
  readonly reasoningCostMultiplier: string;
  readonly enabled: boolean;
  readonly advancedOpen: boolean;
}

export function minutesToClock(minute: number): string {
  const clamped = Math.max(0, Math.min(1440, Math.trunc(minute)));
  const hours = Math.floor(clamped / 60);
  const mins = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export function clockToMinutes(clock: string): number | null {
  const match = /^([01]\d|2[0-4]):([0-5]\d)$/.exec(clock.trim());
  if (match === null || match[1] === undefined || match[2] === undefined) return null;
  const hours = Number(match[1]);
  const mins = Number(match[2]);
  if (hours === 24) return mins === 0 ? 1440 : null;
  return hours * 60 + mins;
}

export function bitOn(mask: number, bit: number): boolean {
  return (mask & (1 << bit)) !== 0;
}

export function toggleBit(mask: number, bit: number): number {
  return mask ^ (1 << bit);
}

export function emptyWindowDraft(key: string): WindowDraft {
  return {
    key,
    windowId: undefined,
    label: '',
    timezone: 'UTC',
    daysOfWeek: 127,
    startClock: '00:00',
    endClock: '01:00',
    costMultiplier: '1',
    inputCostMultiplier: '',
    outputCostMultiplier: '',
    cacheReadCostMultiplier: '',
    cacheWriteCostMultiplier: '',
    reasoningCostMultiplier: '',
    enabled: true,
    advancedOpen: false,
  };
}

function optionalMultiplierText(value: number | null): string {
  return value === null ? '' : String(value);
}

export function windowToDraft(window: UpstreamModelCostWindow, key: string): WindowDraft {
  const hasDimension =
    window.input_cost_multiplier !== null ||
    window.output_cost_multiplier !== null ||
    window.cache_read_cost_multiplier !== null ||
    window.cache_write_cost_multiplier !== null ||
    window.reasoning_cost_multiplier !== null;
  return {
    key,
    windowId: window.window_id,
    label: window.label,
    timezone: window.timezone,
    daysOfWeek: window.days_of_week,
    startClock: minutesToClock(window.start_minute),
    endClock: minutesToClock(window.end_minute),
    costMultiplier: String(window.cost_multiplier),
    inputCostMultiplier: optionalMultiplierText(window.input_cost_multiplier),
    outputCostMultiplier: optionalMultiplierText(window.output_cost_multiplier),
    cacheReadCostMultiplier: optionalMultiplierText(window.cache_read_cost_multiplier),
    cacheWriteCostMultiplier: optionalMultiplierText(window.cache_write_cost_multiplier),
    reasoningCostMultiplier: optionalMultiplierText(window.reasoning_cost_multiplier),
    enabled: window.enabled,
    advancedOpen: hasDimension,
  };
}

function parseOptionalMultiplier(raw: string): number | null | undefined {
  if (raw.trim().length === 0) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_UPSTREAM_SKU_COST_MULTIPLIER) {
    return undefined;
  }
  return parsed;
}

function isValidRequiredMultiplier(raw: string): boolean {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= MAX_UPSTREAM_SKU_COST_MULTIPLIER;
}

const DIMENSION_DRAFT_FIELDS = [
  'inputCostMultiplier',
  'outputCostMultiplier',
  'cacheReadCostMultiplier',
  'cacheWriteCostMultiplier',
  'reasoningCostMultiplier',
] as const;

export type DraftProblemField =
  | 'label'
  | 'timezone'
  | 'days'
  | 'start'
  | 'end'
  | 'costMultiplier'
  | (typeof DIMENSION_DRAFT_FIELDS)[number];

export type DraftProblemReason =
  | 'label_required'
  | 'timezone_required'
  | 'days_required'
  | 'clock_invalid'
  | 'start_end_equal'
  | 'start_not_end_of_day'
  | 'multiplier_invalid';

export interface DraftProblem {
  readonly field: DraftProblemField;
  readonly reason: DraftProblemReason;
}

export function draftProblems(draft: WindowDraft): readonly DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (draft.label.trim().length === 0) problems.push({ field: 'label', reason: 'label_required' });
  if (draft.timezone.trim().length === 0) problems.push({ field: 'timezone', reason: 'timezone_required' });
  if (draft.daysOfWeek < 1 || draft.daysOfWeek > 127) {
    problems.push({ field: 'days', reason: 'days_required' });
  }
  const start = clockToMinutes(draft.startClock);
  const end = clockToMinutes(draft.endClock);
  if (start === null) {
    problems.push({ field: 'start', reason: 'clock_invalid' });
  } else if (start === 1440) {
    problems.push({ field: 'start', reason: 'start_not_end_of_day' });
  }
  if (end === null) problems.push({ field: 'end', reason: 'clock_invalid' });
  if (start !== null && end !== null && start < 1440 && start === end) {
    problems.push({ field: 'start', reason: 'start_end_equal' });
    problems.push({ field: 'end', reason: 'start_end_equal' });
  }
  if (!isValidRequiredMultiplier(draft.costMultiplier)) {
    problems.push({ field: 'costMultiplier', reason: 'multiplier_invalid' });
  }
  for (const field of DIMENSION_DRAFT_FIELDS) {
    if (parseOptionalMultiplier(draft[field]) === undefined) {
      problems.push({ field, reason: 'multiplier_invalid' });
    }
  }
  return problems;
}

export function draftCrossesMidnight(draft: WindowDraft): boolean {
  const start = clockToMinutes(draft.startClock);
  const end = clockToMinutes(draft.endClock);
  if (start === null || end === null || start === 1440) return false;
  return start > end;
}

export function draftToInput(draft: WindowDraft): UpstreamModelCostWindowInput | null {
  if (draftProblems(draft).length > 0) return null;
  const start = clockToMinutes(draft.startClock);
  const end = clockToMinutes(draft.endClock);
  if (start === null || end === null) return null;
  const input = parseOptionalMultiplier(draft.inputCostMultiplier);
  const output = parseOptionalMultiplier(draft.outputCostMultiplier);
  const cacheRead = parseOptionalMultiplier(draft.cacheReadCostMultiplier);
  const cacheWrite = parseOptionalMultiplier(draft.cacheWriteCostMultiplier);
  const reasoning = parseOptionalMultiplier(draft.reasoningCostMultiplier);
  if (
    input === undefined ||
    output === undefined ||
    cacheRead === undefined ||
    cacheWrite === undefined ||
    reasoning === undefined
  ) {
    return null;
  }
  return {
    ...(draft.windowId === undefined ? {} : { window_id: draft.windowId }),
    label: draft.label.trim(),
    timezone: draft.timezone.trim(),
    days_of_week: draft.daysOfWeek,
    start_minute: start,
    end_minute: end,
    cost_multiplier: Number(draft.costMultiplier),
    ...(input === null ? {} : { input_cost_multiplier: input }),
    ...(output === null ? {} : { output_cost_multiplier: output }),
    ...(cacheRead === null ? {} : { cache_read_cost_multiplier: cacheRead }),
    ...(cacheWrite === null ? {} : { cache_write_cost_multiplier: cacheWrite }),
    ...(reasoning === null ? {} : { reasoning_cost_multiplier: reasoning }),
    enabled: draft.enabled,
  };
}

export function draftsToInputs(drafts: readonly WindowDraft[]): readonly UpstreamModelCostWindowInput[] | null {
  const windows: UpstreamModelCostWindowInput[] = [];
  for (const draft of drafts) {
    const input = draftToInput(draft);
    if (input === null) return null;
    windows.push(input);
  }
  return windows;
}

/** Clock + scalar window used by SKU sale schedules (no dimension overlays). */
export interface ScalarScheduleWindow {
  readonly window_id: string;
  readonly label: string;
  readonly timezone: string;
  readonly days_of_week: number;
  readonly start_minute: number;
  readonly end_minute: number;
  readonly multiplier: number;
  readonly enabled: boolean;
}

export interface ScalarScheduleWindowInput {
  readonly window_id?: string;
  readonly label: string;
  readonly timezone: string;
  readonly days_of_week: number;
  readonly start_minute: number;
  readonly end_minute: number;
  readonly multiplier: number;
  readonly enabled?: boolean;
}

export function scalarWindowToDraft(window: ScalarScheduleWindow, key: string): WindowDraft {
  return windowToDraft({
    window_id: window.window_id,
    label: window.label,
    timezone: window.timezone,
    days_of_week: window.days_of_week,
    start_minute: window.start_minute,
    end_minute: window.end_minute,
    cost_multiplier: window.multiplier,
    input_cost_multiplier: null,
    output_cost_multiplier: null,
    cache_read_cost_multiplier: null,
    cache_write_cost_multiplier: null,
    reasoning_cost_multiplier: null,
    enabled: window.enabled,
  }, key);
}

export function draftsToScalarInputs(
  drafts: readonly WindowDraft[],
): readonly ScalarScheduleWindowInput[] | null {
  const inputs = draftsToInputs(drafts);
  if (inputs === null) return null;
  return inputs.map((input) => ({
    ...(input.window_id === undefined ? {} : { window_id: input.window_id }),
    label: input.label,
    timezone: input.timezone,
    days_of_week: input.days_of_week,
    start_minute: input.start_minute,
    end_minute: input.end_minute,
    multiplier: input.cost_multiplier,
    enabled: input.enabled,
  }));
}
